import type { TrailerWithAxlesResponse } from '@contracts/trailer/TrailerWithAxlesResponse';
import type { Client } from '@src/clients/domain/Client';
import { Drawer } from '@src/common/components/Drawer';
import { PageLayout } from '@src/common/components/Page';
import { useToast } from '@src/common/hooks/useToast';
import type { TrailerListing } from '@src/trailer/domain/TrailerList';
import { useTrailer } from '@src/trailer/hooks/useTrailer';
import { useUpdateTrailer } from '@src/trailer/hooks/useUpdateTrailer';
import { TrailerPageForm } from '@src/trailer/TrailerPageForm';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  client: Client;
  trailerId: string;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (updatedTrailer: TrailerWithAxlesResponse) => void;
}

export const EditTrailerDrawer = ({
  isOpen,
  client,
  onUpdate,
  onClose,
  trailerId,
}: Props) => {
  const { t } = useTranslation();

  return (
    <Drawer open={isOpen} onClose={onClose} width="90vw">
      <PageLayout title={t('trailerInformation')}>
        <TrailerLoader client={client} trailerId={trailerId}>
          {(trailer) => (
            <EditTrailer
              client={client}
              trailer={trailer}
              onUpdate={onUpdate}
              onClose={onClose}
            />
          )}
        </TrailerLoader>
      </PageLayout>
    </Drawer>
  );
};

const EditTrailer = ({
  client,
  trailer,
  onUpdate,
  onClose,
}: {
  client: Client;
  trailer: TrailerListing;
  onClose: () => void;
  onUpdate: (updatedTrailer: TrailerWithAxlesResponse) => void;
}) => {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToast();

  const { updateTrailer, isLoading } = useUpdateTrailer({
    clientId: client.id,
    trailer,
    onSuccess: (newTrailer) => {
      showSuccessToast(
        t('editTrailerSuccess', { number: trailer?.unitNumber }),
      );
      onUpdate(newTrailer);
    },
    onError: () => {
      showErrorToast(t('editTrailerError', { number: trailer?.unitNumber }));
    },
  });
  return (
    <TrailerPageForm
      onSubmit={updateTrailer}
      onCancel={onClose}
      isLoading={isLoading}
      client={client}
      prefillData={trailer}
    />
  );
};

const TrailerLoader = ({
  client,
  trailerId,
  children,
}: {
  client: Client;
  trailerId: string;
  children: (trailer: TrailerListing) => ReactNode;
}) => {
  const { trailer, isLoading: isLoadingTrailer } = useTrailer(
    client.id,
    trailerId,
  );

  if (isLoadingTrailer || !trailer) {
    return;
  }

  return children(trailer);
};
